import { Button, useDisclosure } from "@chakra-ui/react";
import { TableBody } from "@components/modules/common/table/TableBody";
import { Card, LoadingOverlay } from "@components/ui-kit";
import { type TColumnDef, useTable } from "@components/ui-kit/table";
import { ModalTransferCredit } from "@components/widgets/Modals/ModalTransferCredit";
import { Trans, t } from "@lingui/macro";
import { currency, dateTime } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import cx from "classnames";
import { observer } from "mobx-react-lite";
import React, { type FunctionComponent, useContext } from "react";
import { PageCreditAccountCtx } from "../../PageCreditAccountDetail";
import { OriginColumn, type OriginColumnProps } from "./OriginColumn";

const RowTransferCreditCtx = React.createContext<{
  creditAccountId: string;
  brandId: string;
  credit: number;
  onTransfer: () => void;
}>(null as any);

const RowTransferCredit: FunctionComponent<
  React.PropsWithChildren<{
    outgoingInvoiceId?: string;
    outgoingInvoiceAmount?: number;
  }>
> = observer(function RowTransferCredit({ outgoingInvoiceId }) {
  const { workspaceStore } = useStore();
  const ctx = useContext(RowTransferCreditCtx);
  const modalTransferCredit = useDisclosure();

  if (!ctx.creditAccountId || !ctx.brandId || !ctx.credit) {
    console.warn(`Missing props`, ctx);
    return null;
  }

  return workspaceStore.settings?.transfer_credit_directly_from_invoice ? (
    <React.Fragment>
      <Button onClick={modalTransferCredit.onOpen}>
        <Trans>Transfer credit</Trans>
      </Button>
      <ModalTransferCredit
        {...modalTransferCredit}
        brandCreditAccountId={ctx.creditAccountId}
        outgoingInvoiceId={outgoingInvoiceId}
        onTransfer={ctx.onTransfer}
      />
    </React.Fragment>
  ) : null;
});

type TData = {
  id: string;
  date: string;
  origin: OriginColumnProps;
  amount: number;
  actions: null;
};

const cols: TColumnDef<TData, any> = [
  {
    key: "date",
    header: () => t`Date`,
    render: (r) => (r.date ? dateTime.format(new Date(r.date)) : ""),
  },
  {
    key: "origin",
    header: () => t`Origin`,
    render: (r) => <OriginColumn {...r.origin} />,
  },
  {
    key: "amount",
    header: () => t`Amount`,
    render: (r) => {
      return (
        <span
          className={cx({
            "text-red": Math.sign(r.amount) === -1,
            "text-green": Math.sign(r.amount) === 1,
          })}
        >
          {currency.formatByWorkspace(r.amount)}
        </span>
      );
    },
  },
  {
    key: "actions",
    header: () => t`Actions`,
    type: "numeric",
    render: (r) => (
      <RowTransferCredit
        outgoingInvoiceId={r.id}
        outgoingInvoiceAmount={r.amount}
      />
    ),
  },
];

export const TableCredits = observer(function TableCredits() {
  const store = useContext(PageCreditAccountCtx);

  const data: TData[] =
    store.creditAccount?.credits.map((i) => ({
      id: i.id,
      amount: i.credit,
      date: i.date,
      origin: {
        outgoingInvoice: i.outgoingInvoice,
        originatesBrandCreditAccount: i.originatesBrandCreditAccount,
      },
      actions: null,
    })) || [];

  const { table, selection } = useTable(data, cols, {
    selectable: false,
    allRowsIds: [],
  });

  return (
    <div data-testid="card-list-credits">
      <Card>
        <div className="h-24 flex items-center justify-between px-6">
          <div className="flex items-center h-full">
            <h2 className="text-3xl font-bold mr-16">
              <Trans>Credits</Trans>
            </h2>
          </div>
        </div>

        <LoadingOverlay isLoading={!store.creditAccount}>
          {store.creditAccount && (
            <RowTransferCreditCtx.Provider
              value={{
                creditAccountId: store.creditAccount.creditAccount.id,
                brandId: store.creditAccount.brand.id,
                credit: store.creditAccount.stats.remaining_credit,
                onTransfer() {
                  store.refetch?.();
                },
              }}
            >
              <TableBody table={table} selection={selection} />
            </RowTransferCreditCtx.Provider>
          )}
        </LoadingOverlay>
      </Card>
    </div>
  );
});
